import { Autoplay, EffectFade } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { Resource } from '@/shared/ui/Resource'

import { DigitalProductPartnersCard } from '../Card'

import 'swiper/css'
import 'swiper/css/effect-fade'

type PartnerRecord = Record<string, unknown> & {
  id: number
  point_reward?: number
  coin_reward?: number
}

type PartnersTopLikesCarouselProps = {
  partners: PartnerRecord[]
  topLikeIds: number[]
}

const shadowBleedClassName = '-mx-2 px-2 pb-1 pt-1'

const renderPartnerCard = (partner: PartnerRecord) => (
  <Resource.Root
    type="Partner"
    id={partner.id}
    pointsReward={partner.point_reward}
    coinsReward={partner.coin_reward}
  >
    <DigitalProductPartnersCard isHighlight {...partner} />
  </Resource.Root>
)

/**
 * Destaque "Top curtidas" — cards roxos empilhados com fade entre slides.
 * O wrapper com padding negativo/margem dá folga pra sombra do featured card
 * sem o `overflow: hidden` padrão do Swiper cortar o blur.
 */
export const PartnersTopLikesCarousel = ({
  partners,
  topLikeIds
}: PartnersTopLikesCarouselProps) => {
  const topLikeOrder = new Map(topLikeIds.map((id, index) => [id, index]))

  const topLikesPartners = partners
    .filter(partner => topLikeIds.includes(partner.id))
    .sort((left, right) => {
      const leftLikes =
        (left.reactions as { like?: number } | undefined)?.like ?? 0
      const rightLikes =
        (right.reactions as { like?: number } | undefined)?.like ?? 0
      const likeDiff = rightLikes - leftLikes
      if (likeDiff !== 0) return likeDiff

      return (
        (topLikeOrder.get(left.id) ?? Number.MAX_SAFE_INTEGER) -
        (topLikeOrder.get(right.id) ?? Number.MAX_SAFE_INTEGER)
      )
    })

  if (topLikesPartners.length === 0) return null

  if (topLikesPartners.length === 1) {
    return (
      <div className={shadowBleedClassName}>
        <div className="pb-2">{renderPartnerCard(topLikesPartners[0])}</div>
      </div>
    )
  }

  return (
    <div className={shadowBleedClassName}>
      <Swiper
        modules={[EffectFade, Autoplay]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        slidesPerView={1}
        className="w-full !overflow-visible [&_.swiper-slide]:!h-auto [&_.swiper-wrapper]:items-stretch"
        loop
        autoplay={{ delay: 4000, disableOnInteraction: false }}
      >
        {topLikesPartners.map(partner => (
          <SwiperSlide key={partner.id} className="!h-auto !overflow-visible pb-2">
            {renderPartnerCard(partner)}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}

PartnersTopLikesCarousel.displayName = 'PartnersTopLikesCarousel'
